/**
 * ContentEditor — reusable CRUD UI for the 3 doctor content types:
 *   • Article       — title, summary, external URL, optional cover image
 *   • Case Study    — title, treatment, duration, description, before/after
 *   • Testimonial   — patient name, quote, rating, treatment, date, anonymous
 *
 * Consumed by:
 *   - Doctor portal:  LandingPageEditor.jsx (kind endpoint: /doctor/content/*)
 *   - Admin portal:   admin DoctorProfile   (kind endpoint: /admin/doctors/:id/content/*)
 *
 * The parent decides which endpoint set to hit via the `apiBase` prop:
 *   • Doctor:  { list: (k) => `/doctor/content/${k}`,
 *                createUrl: (k) => `/doctor/content/${k}`,
 *                itemUrl: (id) => `/doctor/content/item/${id}`,
 *                assetUrl: () => `/doctor/content/asset` }
 *   • Admin:   analogous under /admin/doctors/:id/*
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { confirmDialog } from "@/lib/confirm";
import {
    Plus, Trash, PencilSimple, X, FloppyDisk, Camera, ArrowLeft,
    Article, Sparkle, ChatCircleText, Star, ImageSquare, Newspaper,
    PaperPlaneTilt, Warning,
} from "@phosphor-icons/react";

const KIND_META = {
    article: {
        label:      "Articles",
        singular:   "Article",
        icon:       Newspaper,
        emptyIcon:  Article,
        emptyText:  "Share educational articles you've written or found useful.",
        limit:      100,
    },
    case_study: {
        label:      "Case Studies",
        singular:   "Case Study",
        icon:       Sparkle,
        emptyIcon:  Sparkle,
        emptyText:  "Show real transformations you've delivered. Before/after images speak louder than words.",
        limit:      100,
    },
    testimonial: {
        label:      "Testimonials",
        singular:   "Testimonial",
        icon:       ChatCircleText,
        emptyIcon:  ChatCircleText,
        emptyText:  "Add quotes from happy patients (with consent). Nothing builds trust faster.",
        limit:      100,
    },
};

/* iter100.25 — Editorial workflow. iter100.171 — all doctor-authored
   public content (articles, case studies, testimonials) requires
   editorial review before going live. This map drives the status chip
   color + label + description text shown to the doctor on each row of
   the editor. */
const STATUS_META = {
    draft: {
        label: "Draft",
        chip: "bg-slate-100 text-slate-700 border-slate-200",
        desc: "Only you can see this. Submit for review to publish.",
    },
    pending_review: {
        label: "Pending review",
        chip: "bg-amber-100 text-amber-800 border-amber-200",
        desc: "Waiting for Invisique™ to review — usually within 24 hours.",
    },
    published: {
        label: "Published",
        chip: "bg-emerald-100 text-emerald-800 border-emerald-200",
        desc: "Live on your public page.",
    },
    needs_changes: {
        label: "Needs changes",
        chip: "bg-orange-100 text-orange-800 border-orange-200",
        desc: "The reviewer left feedback below. Edit and re-submit.",
    },
    rejected: {
        label: "Rejected",
        chip: "bg-red-100 text-red-800 border-red-200",
        desc: "Not approved for publishing. See reviewer comment below.",
    },
};

const KINDS_REQUIRING_REVIEW = new Set(["article", "case_study", "testimonial"]);
function effectiveStatus(item) {
    // Backward-compat — rows without a status field predate the workflow
    // and are treated as published.
    return item?.status || "published";
}

async function compressImage(file, maxDim = 1400, quality = 0.82) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
            const w = Math.round(img.width * scale);
            const h = Math.round(img.height * scale);
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            canvas.getContext("2d").drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL("image/jpeg", quality));
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
    });
}

export default function ContentEditor({ kind, apiBase }) {
    const meta = KIND_META[kind];
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | "new" | item.id

    // iter100.46d — Detect doctor-scope calls (vs admin-scope) by the
    // `/doctor/` URL prefix. Articles + case studies in doctor scope
    // route the Add / Edit / Draft-this actions to the dedicated
    // full-page ContentEditorPage (same tab, react-router Link).
    // Testimonials + every admin-scope flow keep the inline form.
    const isDoctorScope = String(apiBase.list?.(kind) || "").startsWith("/doctor/");
    const useFullPageEditor = isDoctorScope && (kind === "article" || kind === "case_study");
    const fullEditorPathFor = (id) => `/doctor/content/${kind}/${id || "new"}${id ? "/edit" : ""}`;

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(apiBase.list(kind));
            setItems(Array.isArray(data) ? data : []);
        } catch {
            toast.error(`Could not load ${meta.label.toLowerCase()}`);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [kind, apiBase]);

    const startNew = () => {
        if (items.length >= meta.limit) {
            toast.error(`You've reached the ${meta.limit}-${meta.singular.toLowerCase()} limit.`);
            return;
        }
        setEditing("new");
    };
    const cancel = () => setEditing(null);

    const submit = async (data, itemId) => {
        try {
            if (itemId && itemId !== "new") {
                await api.put(apiBase.itemUrl(itemId), { data });
                toast.success(`${meta.singular} updated`);
            } else {
                await api.post(apiBase.createUrl(kind), { data });
                toast.success(`${meta.singular} added`);
            }
            setEditing(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save");
        }
    };

    const remove = async (itemId, title) => {
        const ok = await confirmDialog({
            title: `Delete ${meta.singular.toLowerCase()}?`,
            body: `"${title || "This entry"}" will be permanently removed from your public page.`,
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        try {
            await api.delete(apiBase.itemUrl(itemId));
            toast.success(`${meta.singular} removed`);
            await load();
        } catch {
            toast.error("Could not delete");
        }
    };

    /* iter100.25 — Submit-for-review. Only present on the doctor
       apiBase; admin apiBase omits submitUrl since admins approve, not
       submit. Kicks a draft/needs_changes/rejected row into
       pending_review and refreshes the list. */
    const submitForReview = async (itemId, title) => {
        const ok = await confirmDialog({
            title: `Submit for review?`,
            body: `"${title || "This entry"}" will be sent to the Invisique™ review team. You'll be notified once it's approved (usually within 24 hours).`,
            confirmLabel: "Submit",
        });
        if (!ok) return;
        try {
            await api.post(apiBase.submitUrl(itemId));
            toast.success("Sent for review");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not submit");
        }
    };

    if (loading) {
        return <div className="p-6 text-center text-sm text-muted-foreground">Loading {meta.label.toLowerCase()}…</div>;
    }

    return (
        <div className="space-y-4" data-testid={`content-editor-${kind}`}>
            {/* Header row */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-display text-lg font-bold flex items-center gap-2">
                        <meta.icon size={18} weight="duotone" className="text-primary" /> {meta.label}
                    </h3>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        {items.length} {items.length === 1 ? meta.singular.toLowerCase() : meta.label.toLowerCase()}
                    </div>
                </div>
                {editing === null && items.length < meta.limit && (
                    useFullPageEditor ? (
                        <Button asChild size="sm" data-testid={`content-add-${kind}`}>
                            <Link to={fullEditorPathFor(null)}>
                                <Plus size={14} /> Add {meta.singular.toLowerCase()}
                            </Link>
                        </Button>
                    ) : (
                        <Button size="sm" onClick={startNew} data-testid={`content-add-${kind}`}>
                            <Plus size={14} /> Add {meta.singular.toLowerCase()}
                        </Button>
                    )
                )}
            </div>

            {/* Editor (new) */}
            {editing === "new" && (
                <ContentForm
                    kind={kind}
                    initial={null}
                    apiBase={apiBase}
                    onCancel={cancel}
                    onSubmit={(d) => submit(d, "new")}
                />
            )}

            {/* Empty state */}
            {items.length === 0 && editing !== "new" && (
                <div className="rounded-xl border border-dashed border-border bg-muted/30 p-8 text-center">
                    <meta.emptyIcon size={28} weight="duotone" className="text-muted-foreground mx-auto mb-2" />
                    <div className="text-sm text-muted-foreground max-w-md mx-auto leading-snug">{meta.emptyText}</div>
                    {useFullPageEditor ? (
                        <Button asChild size="sm" className="mt-4">
                            <Link to={fullEditorPathFor(null)}>
                                <Plus size={14} /> Add your first {meta.singular.toLowerCase()}
                            </Link>
                        </Button>
                    ) : (
                        <Button size="sm" onClick={startNew} className="mt-4">
                            <Plus size={14} /> Add your first {meta.singular.toLowerCase()}
                        </Button>
                    )}
                </div>
            )}

            {/* Item list */}
            {items.map((it) => {
                const status = effectiveStatus(it);
                const statusMeta = STATUS_META[status] || STATUS_META.draft;
                const canSubmit = !!apiBase.submitUrl
                    && KINDS_REQUIRING_REVIEW.has(kind)
                    && ["draft", "needs_changes", "rejected"].includes(status);
                const isLocked = status === "pending_review";
                return (
                    <div key={it.id} className="rounded-xl border border-border bg-card overflow-hidden" data-testid={`content-item-${it.id}`}>
                        {/* Status strip — shown for every content kind. All
                            doctor-authored public content passes through
                            editorial review since iter100.171 (previously
                            articles auto-published). */}
                        <div className={`px-4 py-2 border-b border-border flex items-center gap-2 flex-wrap ${status === "published" ? "bg-emerald-50/60" : status === "pending_review" ? "bg-amber-50/60" : status === "needs_changes" || status === "rejected" ? "bg-red-50/60" : "bg-slate-50/60"}`}>
                            <span
                                className={`inline-flex items-center gap-1 h-5 px-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${statusMeta.chip}`}
                                data-testid={`content-item-${it.id}-status`}
                            >
                                {statusMeta.label}
                            </span>
                            <span className="text-[11px] text-muted-foreground">{statusMeta.desc}</span>
                            {canSubmit && (
                                <Button
                                    size="sm"
                                    onClick={() => submitForReview(it.id, it.data?.title || it.data?.patient_display_name)}
                                    className="ml-auto h-7 text-[12px]"
                                    data-testid={`content-submit-${it.id}`}
                                >
                                    <PaperPlaneTilt size={12} weight="fill" />
                                    Submit for review
                                </Button>
                            )}
                        </div>

                        {/* Reviewer comment banner — shown when needs_changes or rejected */}
                        {it.review_comment && (status === "needs_changes" || status === "rejected") && (
                            <div className="px-4 py-3 border-b border-red-100 bg-red-50/40 flex items-start gap-2" data-testid={`content-item-${it.id}-review-comment`}>
                                <Warning size={14} weight="fill" className="text-red-600 mt-0.5 shrink-0" />
                                <div className="text-[12px] text-red-900 leading-snug">
                                    <div className="font-bold uppercase tracking-wider text-[10px] mb-0.5">
                                        {status === "rejected" ? "Reviewer comment (rejected)" : "Reviewer feedback"}
                                    </div>
                                    {it.review_comment}
                                </div>
                            </div>
                        )}

                        {editing === it.id ? (
                            <ContentForm
                                kind={kind}
                                initial={it.data}
                                apiBase={apiBase}
                                onCancel={cancel}
                                onSubmit={(d) => submit(d, it.id)}
                            />
                        ) : (
                            <ContentPreview
                                kind={kind}
                                data={it.data}
                                locked={isLocked}
                                editHref={useFullPageEditor ? fullEditorPathFor(it.id) : null}
                                onEdit={useFullPageEditor ? null : () => setEditing(it.id)}
                                onDelete={() => remove(it.id, it.data?.title || it.data?.patient_display_name)}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/* ==========================================================================
   ContentPreview — read-only row that shows what's currently saved.
   ========================================================================== */
function ContentPreview({ kind, data, onEdit, editHref, onDelete, locked }) {
    return (
        <div className="p-4 flex items-start gap-4">
            <div className="flex-1 min-w-0">
                {kind === "article" && (
                    <>
                        <div className="font-display font-bold text-base leading-tight">{data.title}</div>
                        {data.published_at && <div className="text-[11px] text-muted-foreground mt-0.5">{data.published_at}</div>}
                        <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{data.summary}</div>
                        {data.url && <div className="text-xs text-primary mt-1 truncate">🔗 {data.url}</div>}
                        {/* iter100.170 — Nudge cover-less articles. WhatsApp /
                            LinkedIn share previews look bare without an og:image,
                            and the landing-page article row falls back to an
                            icon tile. This subtle hint lifts adoption without
                            being pushy. */}
                        {!data.cover_image && !locked && (
                            <div
                                className="mt-2 inline-flex items-center gap-1.5 h-6 px-2 rounded-md text-[11px] font-semibold bg-amber-50 border border-amber-200 text-amber-800"
                                data-testid="content-item-cover-nudge"
                                title="Articles with a cover image get roughly 2× more link shares because WhatsApp / LinkedIn / X show a big thumbnail preview."
                            >
                                <ImageSquare size={12} weight="fill" />
                                Add a cover image → +2× shares
                            </div>
                        )}
                    </>
                )}
                {kind === "case_study" && (
                    <>
                        <div className="font-display font-bold text-base leading-tight">{data.title}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            {[data.treatment, data.duration].filter(Boolean).join(" · ")}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1">
                            {data.concern_type && (
                                <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                                    {data.concern_type}
                                </span>
                            )}
                            {data.complexity && (
                                <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                                    {data.complexity}
                                </span>
                            )}
                            {data.aligner_count > 0 && (
                                <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                                    {data.aligner_count} aligners
                                </span>
                            )}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{data.description}</div>
                        <div className="mt-2 flex gap-2">
                            {data.before_image && <img src={data.before_image} alt="Before" className="h-10 w-10 rounded object-cover border" />}
                            {data.after_image  && <img src={data.after_image}  alt="After"  className="h-10 w-10 rounded object-cover border" />}
                        </div>
                    </>
                )}
                {kind === "testimonial" && (
                    <>
                        <div className="flex items-center gap-1.5 mb-1">
                            <div className="font-semibold text-sm">{data.patient_display_name}</div>
                            {data.is_anonymous && (
                                <span className="text-[10px] bg-muted px-1.5 rounded uppercase tracking-wider font-bold text-muted-foreground">Anonymous</span>
                            )}
                            {data.rating > 0 && (
                                <div className="flex ml-2">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <Star key={n} size={11} weight={n <= data.rating ? "fill" : "regular"} className={n <= data.rating ? "text-amber-500" : "text-muted-foreground"} />
                                    ))}
                                </div>
                            )}
                        </div>
                        <p className="text-sm text-muted-foreground italic line-clamp-3">&ldquo;{data.quote}&rdquo;</p>
                        {(data.treatment || data.treatment_date) && (
                            <div className="text-[11px] text-muted-foreground mt-1">
                                {[data.treatment, data.treatment_date].filter(Boolean).join(" · ")}
                            </div>
                        )}
                    </>
                )}
            </div>
            <div className="shrink-0 flex flex-col gap-1">
                {editHref ? (
                    <Button asChild size="sm" variant="ghost" disabled={locked} title={locked ? "Cannot edit while under review" : "Edit"} data-testid="content-edit">
                        <Link to={locked ? "#" : editHref} aria-disabled={locked}>
                            <PencilSimple size={14} />
                        </Link>
                    </Button>
                ) : (
                    <Button size="sm" variant="ghost" onClick={onEdit} disabled={locked} title={locked ? "Cannot edit while under review" : "Edit"} data-testid="content-edit">
                        <PencilSimple size={14} />
                    </Button>
                )}
                <Button size="sm" variant="ghost" onClick={onDelete} disabled={locked} title={locked ? "Cannot delete while under review" : "Delete"} data-testid="content-delete">
                    <Trash size={14} className="text-destructive" />
                </Button>
            </div>
        </div>
    );
}

/* ==========================================================================
   ContentForm — the CREATE + EDIT form. Uses `kind` to render kind-specific
   fields, uploads images via `apiBase.assetUrl()`.
   ========================================================================== */
function ContentForm({ kind, initial, apiBase, onCancel, onSubmit }) {
    const [data, setData] = useState(() => initial ? { ...initial } : defaultFor(kind));
    const [saving, setSaving] = useState(false);

    const patch = (k, v) => setData((d) => ({ ...d, [k]: v }));

    const uploadImage = async (assetKey, file) => {
        if (!file || !file.type.startsWith("image/")) {
            toast.error("Please pick an image");
            return;
        }
        try {
            const data_url = await compressImage(file, 1400, 0.82);
            const { data: res } = await api.post(apiBase.assetUrl(), { data_url });
            /* iter100.27 — Preview field name derivation. `assetKey` is e.g.
               "before_asset" but the AssetPicker's `preview` prop reads
               `data.before_image` (the "_asset" suffix is intentionally
               dropped in the preview field name to match what the
               backend `_load_content` returns for saved items). Previously
               we wrote to `before_asset_image` which never rendered — the
               upload succeeded server-side but the UI never showed the
               thumbnail, so users assumed it was broken. */
            const previewField = assetKey.replace(/_asset$/, "") + "_image";
            patch(`${assetKey}_id`, res.id);
            patch(previewField, res.data_url);
            toast.success("Image uploaded");
        } catch {
            toast.error("Upload failed");
        }
    };

    const clean = () => {
        // Strip preview-only fields before submit
        const out = { ...data };
        delete out.cover_image;
        delete out.before_image;
        delete out.after_image;
        delete out.photo_image;
        return out;
    };

    const submit = async () => {
        setSaving(true);
        try {
            await onSubmit(clean());
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-4 space-y-3 bg-muted/30" data-testid="content-form">
            {kind === "article" && (
                <>
                    <FormRow label="Title *">
                        <Input value={data.title || ""} onChange={(e) => patch("title", e.target.value)} placeholder="e.g. 5 things to know before starting clear aligners" data-testid="content-article-title" />
                    </FormRow>
                    <FormRow label="Summary *" hint="1–2 sentences that appear on the doctor page.">
                        <Textarea rows={3} value={data.summary || ""} onChange={(e) => patch("summary", e.target.value)} placeholder="A short teaser…" data-testid="content-article-summary" />
                    </FormRow>
                    <FormRow label="External link" hint="Optional — link to a blog post, YouTube video, or PDF.">
                        <Input value={data.url || ""} onChange={(e) => patch("url", e.target.value)} placeholder="https://…" data-testid="content-article-url" />
                    </FormRow>
                    {/* iter100.171 — "Published date" free-text field removed.
                        The real publish timestamp is set when a reviewer
                        approves the article. */}
                    <FormRow label="Cover image" hint="Recommended — used for WhatsApp / LinkedIn share previews.">
                        <AssetPicker
                            preview={data.cover_image}
                            onFile={(f) => uploadImage("cover_asset", f)}
                            onClear={() => { patch("cover_asset_id", null); patch("cover_image", null); }}
                            testid="content-article-cover"
                        />
                    </FormRow>
                </>
            )}

            {kind === "case_study" && (
                <>
                    <FormRow label="Title *">
                        <Input value={data.title || ""} onChange={(e) => patch("title", e.target.value)} placeholder="e.g. Crowded upper front teeth — 8 months" data-testid="content-case-title" />
                    </FormRow>
                    {/* iter100.24 — Structured case-study metadata. Powers the
                        new filter chips + data pills on the public Case
                        Studies tab. Concern-type is the primary filter axis;
                        Aligner count + complexity feed the info pills. */}
                    <div className="grid sm:grid-cols-2 gap-3">
                        <FormRow label="Primary concern" hint="Used to filter case studies on your public page.">
                            <select
                                className="w-full h-9 border border-border rounded-md bg-background px-2 text-sm"
                                value={data.concern_type || ""}
                                onChange={(e) => patch("concern_type", e.target.value || null)}
                                data-testid="content-case-concern"
                            >
                                <option value="">— Select —</option>
                                <option value="crowding">Crowding</option>
                                <option value="spacing">Spacing / Gaps</option>
                                <option value="overbite">Overbite</option>
                                <option value="underbite">Underbite</option>
                                <option value="complex">Complex</option>
                                <option value="other">Other</option>
                            </select>
                        </FormRow>
                        <FormRow label="Complexity level">
                            <select
                                className="w-full h-9 border border-border rounded-md bg-background px-2 text-sm"
                                value={data.complexity || ""}
                                onChange={(e) => patch("complexity", e.target.value || null)}
                                data-testid="content-case-complexity"
                            >
                                <option value="">— Select —</option>
                                <option value="mild">Mild</option>
                                <option value="moderate">Moderate</option>
                                <option value="complex">Complex</option>
                            </select>
                        </FormRow>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <FormRow label="Treatment *">
                            <Input value={data.treatment || ""} onChange={(e) => patch("treatment", e.target.value)} placeholder="e.g. Invisique™ Clear Aligners" data-testid="content-case-treatment" />
                        </FormRow>
                        <FormRow label="Duration">
                            <Input value={data.duration || ""} onChange={(e) => patch("duration", e.target.value)} placeholder="e.g. 8 months" data-testid="content-case-duration" />
                        </FormRow>
                        <FormRow label="Aligner count" hint="e.g. 24">
                            <Input
                                type="number"
                                min={0}
                                max={999}
                                value={data.aligner_count ?? ""}
                                onChange={(e) => patch("aligner_count", e.target.value === "" ? null : Number(e.target.value))}
                                placeholder="e.g. 24"
                                data-testid="content-case-aligner-count"
                            />
                        </FormRow>
                    </div>
                    <FormRow label="Description *" hint="What was the initial issue, treatment approach, and outcome?">
                        <Textarea rows={4} value={data.description || ""} onChange={(e) => patch("description", e.target.value)} placeholder="Tell the story…" data-testid="content-case-description" />
                    </FormRow>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <FormRow label="Before image">
                            <AssetPicker
                                preview={data.before_image}
                                onFile={(f) => uploadImage("before_asset", f)}
                                onClear={() => { patch("before_asset_id", null); patch("before_image", null); }}
                                testid="content-case-before"
                            />
                        </FormRow>
                        <FormRow label="After image">
                            <AssetPicker
                                preview={data.after_image}
                                onFile={(f) => uploadImage("after_asset", f)}
                                onClear={() => { patch("after_asset_id", null); patch("after_image", null); }}
                                testid="content-case-after"
                            />
                        </FormRow>
                    </div>
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                        <label className="flex items-start gap-2 cursor-pointer" data-testid="content-case-consent-label">
                            <input
                                type="checkbox"
                                className="mt-0.5"
                                checked={data.patient_consent_given !== false}
                                onChange={(e) => patch("patient_consent_given", e.target.checked)}
                                data-testid="content-case-consent"
                            />
                            <span className="text-xs text-amber-900 leading-snug">
                                <strong>I confirm written patient consent</strong> to publish these images/details. A small &ldquo;Patient consent&rdquo; badge will appear on the public case for trust.
                            </span>
                        </label>
                    </div>
                </>
            )}

            {kind === "testimonial" && (
                <>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <FormRow label="Patient name *" hint="Or use initials if the patient prefers privacy.">
                            <Input value={data.patient_display_name || ""} onChange={(e) => patch("patient_display_name", e.target.value)} placeholder="e.g. Priya Sharma" data-testid="content-test-name" />
                        </FormRow>
                        <FormRow label="Anonymous display" hint="On the public page, show as 'Priya S.' instead of full name.">
                            <div className="flex items-center gap-2 h-9 pt-1">
                                <Switch
                                    checked={!!data.is_anonymous}
                                    onCheckedChange={(v) => patch("is_anonymous", Boolean(v))}
                                    data-testid="content-test-anon"
                                />
                                <span className="text-xs text-muted-foreground">{data.is_anonymous ? "Anonymous" : "Full name"}</span>
                            </div>
                        </FormRow>
                    </div>
                    <FormRow label="Quote *">
                        <Textarea rows={4} value={data.quote || ""} onChange={(e) => patch("quote", e.target.value)} placeholder="What did they say about their experience?" data-testid="content-test-quote" />
                    </FormRow>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <FormRow label="Rating">
                            <select
                                className="w-full h-9 border border-border rounded-md bg-background px-2 text-sm"
                                value={data.rating || ""}
                                onChange={(e) => patch("rating", e.target.value ? Number(e.target.value) : null)}
                                data-testid="content-test-rating"
                            >
                                <option value="">—</option>
                                {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{"★".repeat(n)} ({n}/5)</option>)}
                            </select>
                        </FormRow>
                        <FormRow label="Treatment">
                            <Input value={data.treatment || ""} onChange={(e) => patch("treatment", e.target.value)} placeholder="e.g. Clear Aligners" data-testid="content-test-treatment" />
                        </FormRow>
                        <FormRow label="Date" hint="YYYY-MM or free text.">
                            <Input value={data.treatment_date || ""} onChange={(e) => patch("treatment_date", e.target.value)} placeholder="e.g. 2026-05" data-testid="content-test-date" />
                        </FormRow>
                    </div>
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                        <strong>Patient consent required:</strong> Only publish testimonials with the patient&apos;s explicit consent. Consider offering the anonymous toggle to protect privacy.
                    </div>
                </>
            )}

            {/* Buttons */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                <Button variant="ghost" size="sm" onClick={onCancel} data-testid="content-cancel">Cancel</Button>
                <Button size="sm" onClick={submit} disabled={saving} data-testid="content-save">
                    <FloppyDisk size={13} /> {saving ? "Saving…" : "Save"}
                </Button>
            </div>
        </div>
    );
}

function defaultFor(kind) {
    if (kind === "article")     return { title: "", summary: "", url: "" };
    if (kind === "case_study")  return { title: "", treatment: "", duration: "", description: "", concern_type: null, complexity: null, aligner_count: null, patient_consent_given: true };
    if (kind === "testimonial") return { patient_display_name: "", quote: "", rating: null, treatment: "", treatment_date: "", is_anonymous: false };
    return {};
}

function FormRow({ label, hint, children }) {
    return (
        <div>
            <Label className="text-xs font-semibold">{label}</Label>
            <div className="mt-1">{children}</div>
            {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
        </div>
    );
}

function AssetPicker({ preview, onFile, onClear, testid }) {
    const fileRef = useRef(null);
    return (
        <div className="flex items-center gap-2">
            {preview ? (
                <div className="relative">
                    <img src={preview} alt="" className="h-16 w-16 rounded-lg object-cover border border-border" />
                    <button
                        type="button"
                        onClick={onClear}
                        className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-white shadow border border-border flex items-center justify-center"
                        title="Remove"
                        data-testid={`${testid}-remove`}
                    >
                        <X size={10} weight="bold" />
                    </button>
                </div>
            ) : (
                <div className="h-16 w-16 rounded-lg border-2 border-dashed border-border flex items-center justify-center">
                    <ImageSquare size={20} weight="duotone" className="text-muted-foreground" />
                </div>
            )}
            <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onFile(f);
                    e.target.value = "";
                }}
                data-testid={`${testid}-input`}
            />
            <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => fileRef.current?.click()}
                data-testid={`${testid}-btn`}
            >
                <Camera size={13} /> {preview ? "Replace" : "Upload"}
            </Button>
        </div>
    );
}
